import type {
  HoleEntryInput,
  RoundSnapshot,
} from "../../domain/round/round-types.js";
import type { SetHoleBonus } from "../../domain/contest/hole-bonus.js";
import type { RoundSyncEngine } from "../sync/round-sync-engine.js";
import {
  requireRound,
  scheduleAutoSync,
  type ActionContext,
} from "./round-workspace-sync.js";

export interface ContestWorkspaceActions {
  setHoleBonus(bonus: SetHoleBonus): Promise<boolean>;
  reviseHoleEntry(
    participantId: string,
    holeNumber: number,
    entry: HoleEntryInput,
    reviewedVersion: number,
  ): Promise<boolean>;
  concedeHole(
    contestId: string,
    holeNumber: number,
    concedingSideId: string,
  ): Promise<boolean>;
  concedeMatch(contestId: string, concedingSideId: string): Promise<boolean>;
  agreeRemainder(
    id: string,
    parentContestId: string,
    holeNumbers: number[],
    importedAgreement?: true,
  ): Promise<boolean>;
}

export function contestActions(
  context: ActionContext,
  run: (action: () => Promise<void>) => Promise<boolean>,
): ContestWorkspaceActions {
  const save = (
    action: (
      engine: RoundSyncEngine,
      roundId: string,
    ) => Promise<RoundSnapshot>,
    message = "Match decision saved on this device.",
  ) =>
    run(async () => {
      if (context.conflicts.length)
        throw new Error(
          "Resolve the score conflict before changing an agreed match.",
        );
      const round = requireRound(context);
      context.setSnapshot(await action(context.engine, round.roundId));
      context.setQueued(true);
      context.setMessage(message);
      scheduleAutoSync(context);
    });
  return {
    setHoleBonus: (bonus) =>
      save(
        (engine, roundId) => engine.setHoleBonus(roundId, bonus),
        "Hole bonus saved.",
      ),
    reviseHoleEntry: (participantId, holeNumber, entry, reviewedVersion) =>
      run(async () => {
        if (context.conflicts.length)
          throw new Error(
            "Resolve the score conflict before reviewing this score.",
          );
        const round = requireRound(context);
        // Like ordinary entry, persist locally first; the versioned journal
        // detects conflicting remote writes without blocking entry on network I/O.
        context.setSnapshot(
          await context.engine.reviseHoleEntry(
            round.roundId,
            participantId,
            holeNumber,
            entry,
            reviewedVersion,
          ),
        );
        context.setQueued(true);
        context.setMessage("Score change saved on this device.");
        scheduleAutoSync(context);
      }),
    concedeHole: (contestId, hole, sideId) =>
      save((engine, roundId) =>
        engine.concedeHole(roundId, contestId, hole, sideId),
      ),
    concedeMatch: (contestId, sideId) =>
      save((engine, roundId) =>
        engine.concedeMatch(roundId, contestId, sideId),
      ),
    agreeRemainder: (id, parentId, holes, importedAgreement) =>
      save((engine, roundId) =>
        engine.agreeRemainder(roundId, id, parentId, holes, importedAgreement),
      ),
  };
}
